import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitLeadForm } from '@/lib/leadForm';

const CUSTOMER_OPTIONS = [
  { value: 'villa_owner', key: 'form.customer.villaOwner' },
  { value: 'construction', key: 'form.customer.construction' },
  { value: 'developer', key: 'form.customer.developer' },
  { value: 'architect', key: 'form.customer.architect' },
  { value: 'investor', key: 'form.customer.investor' },
  { value: 'other', key: 'form.customer.other' },
];

const SOLUTION_OPTIONS = [
  { value: 'single', key: 'form.solutionOpt.single' },
  { value: 'double', key: 'form.solutionOpt.double' },
  { value: 'villa_project', key: 'form.solutionOpt.villaProject' },
  { value: 'winter_garden', key: 'form.solutionOpt.winterGarden' },
  { value: 'combined', key: 'form.solutionOpt.combined' },
  { value: 'custom', key: 'form.solutionOpt.custom' },
];

const STATUS_OPTIONS = [
  { value: 'land', key: 'form.status.land' },
  { value: 'construction', key: 'form.status.construction' },
  { value: 'existing', key: 'form.status.existing' },
  { value: 'none', key: 'form.status.none' },
  { value: 'other', key: 'form.status.other' },
];

const inputClass =
  'w-full rounded-md border border-foreground-700 bg-foreground-900/70 px-4 py-3 text-sm text-background-50 placeholder:text-background-100/40 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

export default function LeadForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('idle');

    const form = e.currentTarget;
    const result = await submitLeadForm(form);

    if (result.ok) {
      setStatus('success');
      form.reset();
      setCharCount(0);
      setFileName(null);
    } else {
      setStatus('error');
      setErrorMsg(result.message || t('form.error'));
    }
    setSubmitting(false);
  };

  return (
    <section id="iletisim" className="bg-foreground-950 py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-2xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {t('s10.title')}
          </h2>
        </div>

        <form
          data-readdy-form
          onSubmit={handleSubmit}
          className="reveal reveal-delay-1 mt-12"
          noValidate
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label htmlFor="lead-name" className="text-sm font-medium text-background-100/80">
                {t('form.name')}
              </label>
              <input
                id="lead-name"
                name="name"
                type="text"
                required
                placeholder={t('form.namePh')}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lead-phone" className="text-sm font-medium text-background-100/80">
                {t('form.phone')}
              </label>
              <input
                id="lead-phone"
                name="phone"
                type="tel"
                required
                placeholder={t('form.phonePh')}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lead-email" className="text-sm font-medium text-background-100/80">
                {t('form.email')}
              </label>
              <input
                id="lead-email"
                name="email"
                type="email"
                required
                placeholder={t('form.emailPh')}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lead-city" className="text-sm font-medium text-background-100/80">
                {t('form.city')}
              </label>
              <input
                id="lead-city"
                name="city"
                type="text"
                placeholder={t('form.cityPh')}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lead-customer" className="text-sm font-medium text-background-100/80">
                {t('form.customerType')}
              </label>
              <select id="lead-customer" name="customer_type" className={inputClass} defaultValue="">
                <option value="" disabled>
                  {t('form.customerType')}
                </option>
                {CUSTOMER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-foreground-950">
                    {t(opt.key)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lead-solution" className="text-sm font-medium text-background-100/80">
                {t('form.solution')}
              </label>
              <select id="lead-solution" name="solution" className={inputClass} defaultValue="">
                <option value="" disabled>
                  {t('form.solution')}
                </option>
                {SOLUTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-foreground-950">
                    {t(opt.key)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <label htmlFor="lead-status" className="text-sm font-medium text-background-100/80">
                {t('form.projectStatus')}
              </label>
              <select id="lead-status" name="project_status" className={inputClass} defaultValue="">
                <option value="" disabled>
                  {t('form.projectStatus')}
                </option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-foreground-950">
                    {t(opt.key)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <label htmlFor="lead-message" className="text-sm font-medium text-background-100/80">
                {t('form.message')}
              </label>
              <textarea
                id="lead-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder={t('form.messagePh')}
                className={`${inputClass} resize-none`}
                onChange={(e) => setCharCount(e.target.value.length)}
              ></textarea>
              <span className="text-xs text-background-100/40">{charCount}/500</span>
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <span className="text-sm font-medium text-background-100/80">
                {t('form.upload')}
              </span>
              <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-foreground-600 bg-foreground-900/40 px-4 py-4 transition-colors hover:border-primary-500">
                <i className="ri-image-add-line text-xl text-primary-400" aria-hidden="true"></i>
                <span className="text-sm text-background-100/60">
                  {fileName ?? t('form.uploadHint')}
                </span>
                <input
                  ref={fileRef}
                  type="file"
                  name="image"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
                />
              </label>
            </div>

            {/* Anti-spam field — not shown to real users */}
            <div className="field-supplemental">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>
          </div>

          {status === 'success' && (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
              <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
              {t('form.success')}
            </div>
          )}
          {status === 'error' && (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-accent-500/50 bg-accent-500/10 px-4 py-3 text-sm text-accent-200">
              <i className="ri-error-warning-line text-lg" aria-hidden="true"></i>
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full whitespace-nowrap rounded-md bg-primary-500 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-foreground-950 transition-colors hover:bg-primary-400 disabled:opacity-60 cursor-pointer"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <i className="ri-loader-4-line" aria-hidden="true"></i>
              </span>
            ) : (
              t('form.submit')
            )}
          </button>
        </form>
      </div>
    </section>
  );
}